import React, { useEffect, useState } from "react"
import { MDBDataTable } from "mdbreact"
import { Row, Col, Card, CardBody, CardTitle, Button, Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Input } from "reactstrap"
import axios from "../../utils/axios";
import { connect } from "react-redux";
import { getProfileImageUrl } from "../../helpers/image_helpers";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

//Import Action to copy breadcrumb items from local state to redux state
import { setBreadcrumbItems } from "../../store/actions";

import "./datatables.scss"

const Subscriptions = (props) => {
    const [subscriptions, setSubscriptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [modal, setModal] = useState(false);
    const [currentSubscription, setCurrentSubscription] = useState(null);
    const [formData, setFormData] = useState({
        user_id: "",
        type: "membership",
        type_id: "",
        payment: {
            amount: "",
            transaction_id: "",
            gateway: "stripe",
            status: "completed",
            currency: "USD"
        },
        trial_days: 7
    });
    
    document.title = "Subscriptions | Equity Circle - Admin Dashboard";

    const breadcrumbItems = [
        { title: "Subscriptions", link: "#" },
        { title: "All Subscriptions", link: "#" },
    ]

    useEffect(() => {
        props.setBreadcrumbItems('Subscriptions', breadcrumbItems)
        fetchSubscriptions();
    }, []);

    const fetchSubscriptions = async () => {
        setLoading(true);
        try {
            const response = await axios.get('/api/subscriptions');
            setSubscriptions(response.data.data);
        } catch (error) {
            setError(error.response?.data?.message || "Failed to fetch subscriptions");
            toast.error("Failed to fetch subscriptions");
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        if (name.includes('.')) {
            const [parent, child] = name.split('.');
            setFormData(prev => ({
                ...prev,
                [parent]: {
                    ...prev[parent],
                    [child]: value
                }
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const method = currentSubscription ? 'put' : 'post';
            const url = currentSubscription 
                ? `/api/subscriptions/${currentSubscription.id}`
                : '/api/subscriptions';
            
            const response = await axios[method](url, formData);
            
            toast.success(`Subscription ${currentSubscription ? 'updated' : 'created'} successfully`);
            toggleModal();
            fetchSubscriptions();
        } catch (error) {
            toast.error(error.response?.data?.message || "Operation failed");
        }
    };

    const handleEdit = (subscription) => {
        setCurrentSubscription(subscription);
        setFormData({
            user_id: subscription.user_id,
            type: subscription.type,
            type_id: subscription.type_id,
            payment: {
                amount: subscription.payment?.payment_amount || "",
                transaction_id: subscription.payment?.payment_transaction_id || "",
                gateway: subscription.payment?.payment_gateway || "stripe",
                status: subscription.payment?.payment_status || "completed",
                currency: subscription.payment?.payment_currency || "USD"
            },
            trial_days: 7
        });
        toggleModal();
    };

    const handleDelete = async (id) => {
        if (window.confirm("Are you sure you want to delete this subscription?")) {
            try {
                await axios.delete(`/api/subscriptions/${id}`);
                toast.success("Subscription deleted successfully");
                fetchSubscriptions();
            } catch (error) {
                toast.error(error.response?.data?.message || "Failed to delete subscription");
            }
        }
    };

    const handleCancel = async (id) => {
        try {
            await axios.post(`/api/subscriptions/cancel/${id}`);
            toast.success("Subscription cancelled successfully");
            fetchSubscriptions();
        } catch (error) {
            toast.error(error.response?.data?.message || "Failed to cancel subscription");
        }
    };

    const toggleModal = () => {
        if (modal) {
            setCurrentSubscription(null);
            setFormData({
                user_id: "",
                type: "membership",
                type_id: "",
                payment: {
                    amount: "",
                    transaction_id: "",
                    gateway: "stripe",
                    status: "completed",
                    currency: "USD"
                },
                trial_days: 7
            });
        }
        setModal(!modal);
    };

    const getStatusBadge = (status) => {
        let badgeClass = "";
        switch (status?.toLowerCase()) {
            case "active":
                badgeClass = "badge bg-success";
                break;
            case "cancelled":
                badgeClass = "badge bg-danger";
                break;
            case "expired":
                badgeClass = "badge bg-warning";
                break;
            default:
                badgeClass = "badge bg-secondary";
        }
        return <span className={badgeClass}>{status || "N/A"}</span>;
    };

    const data = {
        columns: [
            {
                label: "ID",
                field: "id",
                sort: "asc",
                width: 100,
            },
            {
                label: "User",
                field: "user",
                sort: "asc",
                width: 200,
            },
            {
                label: "Type",
                field: "type",
                sort: "asc",
                width: 150,
            },
            {
                label: "Amount",
                field: "amount",
                sort: "asc",
                width: 100,
            },
            {
                label: "Status",
                field: "status",
                sort: "asc",
                width: 100,
            },
            {
                label: "Start Date",
                field: "starts_at",
                sort: "asc",
                width: 150,
            },
            {
                label: "End Date",
                field: "ends_at",
                sort: "asc",
                width: 150,
            },
            {
                label: "Actions",
                field: "actions",
                sort: "disabled",
                width: 200,
            },
        ],
        rows: subscriptions.map((subscription) => ({
            id: `#${subscription.id}`,
            user: subscription.user ? (
                <div className="d-flex align-items-center">
                    <img 
                        src={getProfileImageUrl(subscription.user)} 
                        alt={subscription.user?.name} 
                        className="rounded-circle me-2" 
                        style={{ width: "30px", height: "30px" }} 
                    />
                    <span>{subscription.user?.name || 'N/A'}</span>
                </div>
            ) : 'N/A',
            type: subscription.type ? subscription.type.charAt(0).toUpperCase() + subscription.type.slice(1) : 'N/A',
            amount: subscription.payment ? `$${parseFloat(subscription.payment.payment_amount).toFixed(2)}` : 'N/A',
            status: getStatusBadge(subscription.status),
            starts_at: new Date(subscription.created_at).toLocaleDateString(),
            ends_at: subscription.ends_at ? new Date(subscription.ends_at).toLocaleDateString() : 'N/A',
            actions: (
                <div className="d-flex gap-2">
                    <button 
                        className="btn btn-sm btn-primary"
                        onClick={() => handleEdit(subscription)}
                    >
                        <i className="bx bx-edit"></i>
                    </button>
                    <button 
                        className="btn btn-sm btn-danger"
                        onClick={() => handleDelete(subscription.id)}
                    >
                        <i className="bx bx-trash"></i>
                    </button>
                    {subscription.status === 'active' && (
                        <button 
                            className="btn btn-sm btn-warning"
                            onClick={() => handleCancel(subscription.id)}
                        >
                            <i className="bx bx-x-circle"></i> Cancel
                        </button>
                    )}
                </div>
            )
        })),
    }

    return (
        <React.Fragment>
            <Row>
                <Col className="col-12">
                    <Card>
                        <CardBody>
                            <div className="d-flex justify-content-between align-items-center mb-4">
                                <div>
                                    <CardTitle className="h4 mb-0">Subscription Management</CardTitle>
                                    <p className="card-title-desc mb-0">
                                        View and manage all subscription plans, their status, and payment details.
                                    </p>
                                </div>
                                <Button color="primary" onClick={toggleModal}>
                                    <i className="bx bx-plus me-1"></i> Add Subscription
                                </Button>
                            </div>

                            {loading ? (
                                <div className="text-center">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="sr-only">Loading...</span>
                                    </div>
                                </div>
                            ) : error ? (
                                <div className="alert alert-danger">{error}</div>
                            ) : (
                                <MDBDataTable 
                                    responsive 
                                    bordered 
                                    data={data} 
                                    className="mt-4"
                                    noBottomColumns
                                />
                            )}
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            {/* Add/Edit Subscription Modal */}
            <Modal isOpen={modal} toggle={toggleModal} size="lg">
                <ModalHeader toggle={toggleModal}>
                    {currentSubscription ? 'Edit Subscription' : 'Add New Subscription'}
                </ModalHeader>
                <Form onSubmit={handleSubmit}>
                    <ModalBody>
                        <Row>
                            <Col md={6}>
                                <FormGroup>
                                    <Label>User ID</Label>
                                    <Input 
                                        type="text" 
                                        name="user_id" 
                                        value={formData.user_id} 
                                        onChange={handleInputChange}
                                        required 
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <Label>Type</Label>
                                    <Input 
                                        type="select" 
                                        name="type" 
                                        value={formData.type} 
                                        onChange={handleInputChange}
                                        required
                                    >
                                        <option value="membership">Membership</option>
                                        <option value="event">Event</option>
                                        <option value="course">Course</option>
                                    </Input>
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <Label>Type ID</Label>
                                    <Input 
                                        type="number" 
                                        name="type_id" 
                                        value={formData.type_id} 
                                        onChange={handleInputChange}
                                        required 
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <Label>Amount</Label>
                                    <Input 
                                        type="number" 
                                        name="payment.amount" 
                                        value={formData.payment.amount} 
                                        onChange={handleInputChange}
                                        step="0.01"
                                        required 
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <Label>Transaction ID</Label>
                                    <Input 
                                        type="text" 
                                        name="payment.transaction_id" 
                                        value={formData.payment.transaction_id} 
                                        onChange={handleInputChange}
                                        required 
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <Label>Payment Gateway</Label>
                                    <Input 
                                        type="select" 
                                        name="payment.gateway" 
                                        value={formData.payment.gateway} 
                                        onChange={handleInputChange}
                                        required
                                    >
                                        <option value="stripe">Stripe</option>
                                        <option value="paypal">PayPal</option>
                                        <option value="bank_transfer">Bank Transfer</option>
                                    </Input>
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <Label>Payment Status</Label>
                                    <Input 
                                        type="select" 
                                        name="payment.status" 
                                        value={formData.payment.status} 
                                        onChange={handleInputChange}
                                        required
                                    >
                                        <option value="pending">Pending</option>
                                        <option value="completed">Completed</option>
                                        <option value="failed">Failed</option>
                                        <option value="refunded">Refunded</option>
                                    </Input>
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <Label>Currency</Label>
                                    <Input 
                                        type="text" 
                                        name="payment.currency" 
                                        value={formData.payment.currency} 
                                        onChange={handleInputChange}
                                        required 
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <Label>Trial Days</Label>
                                    <Input 
                                        type="number" 
                                        name="trial_days" 
                                        value={formData.trial_days} 
                                        onChange={handleInputChange}
                                        min="0"
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                    </ModalBody>
                    <ModalFooter>
                        <Button type="button" color="secondary" onClick={toggleModal}>
                            Cancel
                        </Button>
                        <Button type="submit" color="primary">
                            {currentSubscription ? 'Update' : 'Create'} Subscription
                        </Button>
                    </ModalFooter>
                </Form>
            </Modal>
        </React.Fragment>
    )
}

export default connect(null, { setBreadcrumbItems })(Subscriptions);